import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'components/ui-components/Modal';
import Button from 'components/ui-components/Button';
import Input from 'components/cef_form/input';
import ProductInformation from '../product_show/product_information';
import warningIcon from '../../../assets/images/shared/icons/warning-icon.svg';

/**
 * @param {Object} props
 * @param {boolean} props.modalIsOpen - whether the modal is open or not
 * @param {function} props.closeModal - function to close modal
 * @param {function} props.successCallback - function to execute upon adding to basket
 * @param {Array<Object>} props.backOrderItems - items to display on the modal
 * @returns back order modal with populated items
 */
const BackOrderModal = ({
  modalIsOpen,
  closeModal,
  successCallback,
  backOrderItems,
}) => {
  const [isChecked, setChecked] = useState(false);
  const [outOfStockItems, setOutOfStockItems] = useState([]);
  const [insufficientStockItems, setInsufficientStockItems] = useState([]);
  const outOfStockCount = outOfStockItems.length;
  const insufficientStockCount = insufficientStockItems.length;

  useEffect(() => {
    setChecked(false);
  }, [closeModal]);

  const generateItems = () => {
    setInsufficientStockItems([]);
    setOutOfStockItems([]);
    for (let i = 0; i < backOrderItems.length; i++) {
      const stock = backOrderItems[i].stock_level;
      if (stock === 0) {
        setOutOfStockItems(outOfStockItems => [
          ...outOfStockItems,
          backOrderItems[i],
        ]);
      } else {
        setInsufficientStockItems(insufficientStockItems => [
          ...insufficientStockItems,
          backOrderItems[i],
        ]);
      }
    }
  };

  useEffect(() => {
    if (backOrderItems.length > 0) {
      generateItems();
    }
  }, [backOrderItems]);

  return (
    <StyledModal isOpen={modalIsOpen} closeModal={closeModal}>
      <div>
        {insufficientStockCount > 0 && (
          <>
            <ModalHeader className="fl ai-c fw-600 ff-body jc-c m-jc-fs">
              <img
                src={warningIcon}
                className="warning-icon mr-5"
                alt="warning-icon"
                width="27"
                height="30"
              />
              Insufficient Stock
              <ProductCountHeader>
                ({insufficientStockCount}{' '}
                {insufficientStockCount > 1 ? 'products' : 'product'})
              </ProductCountHeader>
            </ModalHeader>

            <ModalContent className="fl">
              {insufficientStockItems.map(
                ({
                  thumbnail,
                  product_name: productName,
                  stock_code: stockCode,
                  part_code: partCode,
                  stock_level: stockLevel,
                  total_basket_and_requested_quantity: totalQuant,
                }) => (
                  <Item className="fl" key={stockCode}>
                    <Thumbnail
                      className="mr-16"
                      src={thumbnail}
                      alt="product"
                      width="50"
                      height="50"
                    />
                    <div>
                      <ProductInformation
                        productName={productName}
                        stockCode={stockCode}
                        partCode={partCode}
                        stockLevel={stockLevel}
                      />
                      <StockWarning>
                        <BoldSpan>{stockLevel} </BoldSpan>
                        will be dispatched with your order. The remaining
                        <BoldSpan> {totalQuant - stockLevel} </BoldSpan>
                        will be dispatched when available. We will notify you
                        with an estimated delivery date.
                      </StockWarning>
                    </div>
                  </Item>
                ),
              )}
            </ModalContent>
          </>
        )}
        {outOfStockCount > 0 && (
          <>
            <ModalHeader className="fl ai-c fw-600 ff-body jc-c m-jc-fs">
              <img
                src={warningIcon}
                className="warning-icon mr-5"
                alt=""
                width="27"
                height="30"
              />
              Out of Stock
              <ProductCountHeader>
                ({outOfStockCount}{' '}
                {outOfStockCount > 1 ? 'products' : 'product'})
              </ProductCountHeader>
            </ModalHeader>

            <ModalContent>
              {outOfStockItems.map(
                ({
                  thumbnail,
                  product_name: productName,
                  stock_code: stockCode,
                  part_code: partCode,
                  stock_level: stockLevel,
                }) => (
                  <Item className="fl" key={stockCode}>
                    <Thumbnail
                      className=" mr-16"
                      src={thumbnail}
                      alt="product"
                      width="50"
                      height="50"
                    />
                    <div>
                      <ProductInformation
                        productName={productName}
                        stockCode={stockCode}
                        partCode={partCode}
                        stockLevel={stockLevel}
                      />
                      <StockWarning>
                        This product is currently
                        <BoldSpan> out of stock </BoldSpan>and will be
                        dispatched when available. We will notify you with an
                        estimated delivery date.
                      </StockWarning>
                    </div>
                  </Item>
                ),
              )}
            </ModalContent>
          </>
        )}
      </div>
      <ModalFooter>
        <div className="mb-16 fl jc-c">
          <ConfirmationContainer>
            <Input
              className="back-order__checkbox mr-5"
              type="checkbox"
              ariaLabel="Confirm Backorder"
              checked={isChecked}
              handleChange={() => {
                setChecked(!isChecked);
              }}
            />
            <p className="ml-6 mb-0">
              I am aware there is insufficient stock and delivery dates will be
              confirmed.
            </p>
          </ConfirmationContainer>
        </div>
        <ButtonContainer>
          <CancelButton
            aria-label="Cancel"
            type="button"
            handleClick={closeModal}
            label="Cancel">
            Cancel
          </CancelButton>
          <AddToBasketButton
            className={`${
              isChecked ? 'cef-btn cef-btn--green' : 'cef-btn--grey-disabled'
            }`}
            type="button"
            aria-label="Add to Basket"
            handleClick={() => {
              if (isChecked) {
                successCallback();
              }
            }}
            label="Add to Basket">
            Add to Basket
          </AddToBasketButton>
        </ButtonContainer>
      </ModalFooter>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  > div {
    padding: 0;
    width: 90%;
    max-width: 530px;
    max-height: 90%;
    overflow: scroll;
    > h2 {
      color: #4c4c4c;
      > span {
        color: #7f7f7f;
      }
    }
  }
`;

const ModalHeader = styled.h2`
  font-size: 1.375rem;
  padding: 1.125rem 0;
  margin: 0;
  @media only screen and (min-width: 768px) {
    padding-left: 2rem;
  }
`;

const ModalContent = styled.ul`
  flex-direction: column;
  margin: 0;
`;

const Item = styled.li`
  border-top: 1px solid #e4e4e4;
  padding: 1.5rem;
`;

const Thumbnail = styled.img`
  width: 50px;
  height: 50px;
`;

const BoldSpan = styled.span`
  font-weight: 700;
`;

const StockWarning = styled.div`
  background-color: #f2f2f2;
  padding: 1rem;
`;

const ModalFooter = styled.div`
  border-top: 1px solid #e4e4e4;
  padding: 1rem;
`;

const ProductCountHeader = styled.span`
  font-weight: 400;
  font-size: 1.125rem
  width: 30%;
  margin-left: 6px;
`;

const ConfirmationContainer = styled.div`
  font-size: 0.875rem;
  display: flex;
  border: 1px solid #d41f3a;
  padding: 0.5rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  @media only screen and (min-width: 768px) {
    justify-content: end;
  }
`;

const CancelButton = styled(Button)`
  background-color: #4c4c4c;
  color: white;
  font-weight: 500;
  border: 0;
  padding: 0;
  width: 48%;
  height: 44px;
  @media only screen and (min-width: 768px) {
    width: 25%;
  }
`;

const AddToBasketButton = styled(Button)`
  font-weight: 500;
  border: 0;
  padding: 0;
  width: 48%;
  height: 44px;
  margin-left: 15px;
  @media only screen and (min-width: 768px) {
    width: 25%;
  }
`;

export default BackOrderModal;
